import { Capacitor } from "@capacitor/core";
import {
  INTRO_ELIGIBILITY_STATUS,
  Purchases,
  type PurchasesPackage,
} from "@revenuecat/purchases-capacitor";

export type TrialEligibility = "eligible" | "ineligible" | "unknown";

/**
 * Best-effort check of whether the user will actually receive the free trial on
 * the given (monthly) package, so the soft paywall can avoid promising "7 days
 * free" to someone the store won't grant a trial to (e.g. an Apple ID / Google
 * account that already consumed it). The store is the source of truth — this only
 * drives copy; the purchase call is unchanged either way.
 *
 * iOS: `checkTrialOrIntroductoryPriceEligibility` reflects Apple's per-group
 * lifetime eligibility. Android always returns UNKNOWN from that API, so we infer
 * eligibility from whether the SDK surfaced a free-trial phase on the product
 * (Google omits the offer's free phase for ineligible accounts).
 *
 * Returns "unknown" on web / when disabled / on any error — callers should treat
 * "unknown" optimistically (show trial copy) rather than hiding a real trial.
 */
export const getMonthlyTrialEligibility = async (
  monthlyPackage: PurchasesPackage | undefined,
  isEnabled: boolean,
): Promise<TrialEligibility> => {
  if (!isEnabled || !monthlyPackage) {
    return "unknown";
  }

  const platform = Capacitor.getPlatform();

  if (platform === "android") {
    const product = monthlyPackage.product;
    const hasFreePhase =
      Boolean(product.defaultOption?.freePhase) ||
      (product.subscriptionOptions ?? []).some((option) => Boolean(option.freePhase));
    return hasFreePhase ? "eligible" : "ineligible";
  }

  if (platform !== "ios") {
    return "unknown";
  }

  try {
    const productId = monthlyPackage.product.identifier;
    const result = await Purchases.checkTrialOrIntroductoryPriceEligibility({
      productIdentifiers: [productId],
    });
    const status = result[productId]?.status;

    switch (status) {
      case INTRO_ELIGIBILITY_STATUS.INTRO_ELIGIBILITY_STATUS_ELIGIBLE:
        return "eligible";
      case INTRO_ELIGIBILITY_STATUS.INTRO_ELIGIBILITY_STATUS_INELIGIBLE:
      case INTRO_ELIGIBILITY_STATUS.INTRO_ELIGIBILITY_STATUS_NO_INTRO_OFFER_EXISTS:
        return "ineligible";
      default:
        return "unknown";
    }
  } catch (error) {
    console.warn("[RevenueCat] Trial eligibility check failed", error);
    return "unknown";
  }
};
